import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { HomeHeader } from '../home/HomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Progress } from '../ui/progress'
import { ChevronLeft, Trash2, Loader2, Download, BookOpenCheck, BookOpen, BookMarked } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { getLanguageByCode, getLanguageFlag } from '../../lib/constants/languages'
import { useTranslation } from 'react-i18next'
import { notifyWalletUpdate } from '../../lib/events/wallet-events'
import { translationEvents } from '../../lib/events/translation-events'

interface Translation {
  id: string
  language: string
  cefr_level: string | null
  file_format: string
  status: string
  is_human_translation: boolean
  created_at: string
}

interface BookData {
  id: string
  title_original: string
  title_international: string
  author_original: string
  author_international: string
  original_language: string | null
  description: string | null
  estimated_pages: number | null
  cover_url: string | null
  original_file_path: string | null
  genre: string | null
  created_at: string
  updated_at: string
  translations: Record<string, Translation[]>
}

export function UserBookDetail() {
  const { t } = useTranslation('common')
  const { bookId, translationId } = useParams<{ bookId: string; translationId?: string }>()
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [book, setBook] = useState<BookData | null>(null)
  const [currentTranslation, setCurrentTranslation] = useState<Translation | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadingOriginal, setDownloadingOriginal] = useState(false)
  const [translationProgress, setTranslationProgress] = useState<number>(0)

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const loadBook = useCallback(async () => {
    if (!bookId) return
    
    try {
      setLoading(true)
      const data = await apiClient.getUserBook(bookId)
      setBook(data.book)
      
      // Find the current translation if translationId is provided
      if (translationId && data.book.translations) {
        const allTranslations: Translation[] = Object.values(data.book.translations).flat() as Translation[]
        const found = allTranslations.find(t => t.id === translationId)
        setCurrentTranslation(found || null)
      } else if (data.book.translations) {
        // If no translationId, redirect to the first available translation
        const allTranslations: Translation[] = Object.values(data.book.translations).flat() as Translation[]
        if (allTranslations.length > 0) {
          // Prefer available translations
          const available = allTranslations.find(t => t.status === 'available')
          const firstTrans = available || allTranslations[0]
          navigate(`/user/books/${bookId}/translations/${firstTrans.id}`, { replace: true })
          return
        }
      }
    } catch (error) {
      console.error('Failed to load book:', error)
      alert(t('book.loadBookFailed'))
      navigate('/my-books')
    } finally {
      setLoading(false)
    }
  }, [bookId, translationId, navigate, t])

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    
    loadBook()
  }, [bookId, translationId, user, loadBook, navigate])

  // Subscribe to translation complete events to refresh book data
  useEffect(() => {
    if (!bookId) return

    const unsubscribe = translationEvents.subscribe((payload) => {
      if (payload.bookId === bookId) {
        loadBook()
      }
    })

    return unsubscribe
  }, [bookId, loadBook])

  // Poll for translation progress when current translation is processing
  useEffect(() => {
    if (!currentTranslation || currentTranslation.status !== 'processing') {
      // Don't reset progress here - it causes a flash of 0% when completing
      return
    }

    let timer: NodeJS.Timeout | null = null

    const fetchProgress = async (): Promise<boolean> => {
      try {
        const data = await apiClient.getActiveTranslations()
        const active = data.translations.find(t => t.translation_id === currentTranslation.id)
        if (active) {
          setTranslationProgress(active.progress_percent)
          return true // Still processing, continue polling
        } else {
          // Translation completed or failed - show 100% briefly before reload
          // The translation event subscription will handle the reload
          setTranslationProgress(100)
          return false // Stop polling
        }
      } catch (error) {
        console.debug('Failed to fetch translation progress:', error)
        return false // Stop polling on error
      }
    }

    const startPolling = async () => {
      // Initial fetch
      const shouldContinue = await fetchProgress()
      
      if (shouldContinue) {
        // Set up interval that stops when translation is no longer active
        timer = setInterval(async () => {
          const shouldContinue = await fetchProgress()
          if (!shouldContinue && timer) {
            clearInterval(timer)
            timer = null
          }
        }, 3000)
      }
    }

    startPolling()
    
    return () => {
      if (timer) {
        clearInterval(timer)
      }
    }
  }, [currentTranslation?.id, currentTranslation?.status])

  const handleDownload = async () => {
    if (!bookId || !currentTranslation || !user) return

    try {
      setDownloading(true)
      await apiClient.downloadUserBook(bookId, currentTranslation.id)
    } catch (error) {
      console.error('Download failed:', error)
      alert(t('book.downloadFailed'))
    } finally {
      setDownloading(false)
    }
  }

  const handleDownloadOriginal = async () => {
    if (!bookId || !user) return

    try {
      setDownloadingOriginal(true)
      await apiClient.downloadUserBookOriginal(bookId)
    } catch (error) {
      console.error('Download original failed:', error)
      alert(t('book.downloadFailed'))
    } finally {
      setDownloadingOriginal(false)
    }
  }

  const handlePreview = () => {
    if (!bookId || !currentTranslation) return
    navigate(`/preview/${bookId}/${currentTranslation.id}`, { 
      state: { isOwnedBook: true } 
    })
  }

  const handleRead = () => {
    if (!bookId || !currentTranslation) return
    navigate(`/read/${bookId}/${currentTranslation.id}`, { 
      state: { from: 'user-book' } 
    })
  }

  const handleDeleteTranslation = async () => {
    if (!currentTranslation || !bookId) return
    
    const allTranslations: Translation[] = book?.translations 
      ? Object.values(book.translations).flat() as Translation[]
      : []
    const isLastTranslation = allTranslations.length <= 1

    const confirmMessage = isLastTranslation
      ? t('book.deleteLastTranslationConfirmation', 'This is the last translation. Deleting it will also delete the book. Are you sure?')
      : t('book.deleteTranslationConfirmation')

    if (!confirm(confirmMessage)) return

    try {
      setDeleting(true)
      
      if (isLastTranslation) {
        await apiClient.deleteUserBook(bookId)
        alert(t('book.bookDeleted'))
        navigate('/my-books')
      } else {
        const result = await apiClient.deleteUserBookTranslation(bookId, currentTranslation.id)
        
        if (result.refunded && result.refund_amount && result.refund_amount > 0) {
          notifyWalletUpdate()
          alert(t('book.translationDeletedWithRefund', { amount: result.refund_amount.toFixed(2) }))
        } else {
          alert(t('book.translationDeleted'))
        }
        
        const remainingTranslations = allTranslations.filter(t => t.id !== currentTranslation.id)
        if (remainingTranslations.length > 0) {
          navigate(`/user/books/${bookId}/translations/${remainingTranslations[0].id}`, { replace: true })
        } else {
          navigate('/my-books')
        }
      }
    } catch (error) {
      console.error('Failed to delete:', error)
      alert(t('book.deleteFailed'))
    } finally {
      setDeleting(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const getLanguageDisplayName = (langCode: string): string => {
    const lang = getLanguageByCode(langCode)
    return lang ? lang.name : langCode
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HomeHeader user={user} onLogout={handleLogout} />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">{t('book.loadingBookDetails')}</p>
        </div>
      </div>
    )
  }

  if (!book || !currentTranslation) {
    return (
      <div className="min-h-screen bg-background">
        <HomeHeader user={user} onLogout={handleLogout} />
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold mb-4">{t('book.translationNotFound', 'Translation not found')}</h2>
          <Button onClick={() => navigate('/my-books')}>{t('book.backToMyBooks')}</Button>
        </div>
      </div>
    )
  }

  // Build meta info string
  const metaParts: string[] = []
  if (book.original_language) {
    metaParts.push(`${t('book.original', 'Original')}: ${book.original_language.toUpperCase()}`)
  }
  if (book.estimated_pages) {
    metaParts.push(`~${book.estimated_pages} ${t('book.pages', 'pages')}`)
  }
  if (book.genre) {
    metaParts.push(book.genre)
  }
  const metaString = metaParts.join(' · ')

  const isAvailable = currentTranslation.status === 'available'
  const isProcessing = currentTranslation.status === 'processing'
  const fileFormat = currentTranslation.file_format.toLowerCase()
  const isEpub = fileFormat === 'epub'
  const isFb2 = fileFormat === 'fb2'
  // Show read/preview buttons based on format (they'll be disabled if not available)
  const supportsOnlineReading = isEpub || isFb2
  const canReadOnline = isAvailable && supportsOnlineReading
  const canPreview = canReadOnline

  return (
    <div className="book-detail-page min-h-screen bg-background pb-24 lg:pb-8">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-4 lg:py-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/my-books')}
          className="back-btn flex items-center text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t('book.backToMyBooks')}
        </button>

        {/* ============ MOBILE LAYOUT ============ */}
        <div className="mobile-layout lg:hidden">
          {/* Header: Cover + Title + Meta */}
          <div className="book-header flex gap-4 mb-6">
            {/* Cover - Compact on mobile */}
            <div className="book-cover flex-shrink-0 w-24">
              <div className="aspect-[2/3] bg-muted rounded-lg overflow-hidden">
                {book.cover_url ? (
                  <img
                    src={book.cover_url}
                    alt={book.title_international}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
              </div>
            </div>

            {/* Title + Meta */}
            <div className="book-info flex-1 min-w-0 flex items-start gap-2">
              <div className="w-1 h-6 bg-primary rounded-full flex-shrink-0 mt-0.5"></div>
              <div className="min-w-0 space-y-1.5">
                <div className="title-author">
                  <h1 className="text-xl font-bold text-foreground line-clamp-2">
                    {book.title_international}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {t('book.by', 'by')} {book.author_international}
                  </p>
                </div>

                {metaString && (
                  <p className="meta-info text-xs text-muted-foreground">
                    {metaString}
                  </p>
                )}

                {/* Badge */}
                <Badge variant="secondary" className="private-library-badge">
                  <BookMarked className="h-3 w-3 mr-1" />
                  {t('library.myGradedBook', 'My Graded Book')}
                </Badge>
              </div>
            </div>
          </div>

          {/* Version Info Card (static, no selectors) */}
          <div className="version-card bg-card rounded-lg border p-4 mb-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">
              {t('book.translationVersion', 'Translation Version')}
            </h3>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">{getLanguageFlag(currentTranslation.language)}</span>
                <span className="font-medium">{getLanguageDisplayName(currentTranslation.language)}</span>
              </div>
              {currentTranslation.cefr_level && (
                <Badge variant="default" className="level-badge">
                  {currentTranslation.cefr_level}
                </Badge>
              )}
              <Badge variant="outline" className="format-badge">
                {currentTranslation.file_format.toUpperCase()}
              </Badge>
            </div>

            {/* Processing status */}
            {isProcessing && (
              <div className="processing-status mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">
                    {t('book.translationInProgress', 'Translation in progress...')}
                  </span>
                  <span className="text-sm font-medium text-primary">{translationProgress}%</span>
                </div>
                <Progress value={translationProgress} className="h-2" />
              </div>
            )}
          </div>

          {/* Description */}
          {book.description && (
            <div className="description-section bg-card rounded-lg border p-4 mb-4">
              <h3 className="text-sm font-medium text-foreground mb-2">{t('common.description')}</h3>
              <p className="text-sm text-muted-foreground">{book.description}</p>
            </div>
          )}

          {/* Download Original + Delete row */}
          <div className="secondary-actions flex items-center justify-between mt-4">
            {book.original_file_path ? (
              <button
                onClick={handleDownloadOriginal}
                disabled={downloadingOriginal}
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                {downloadingOriginal ? (
                  <>
                    <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                    {t('library.downloading')}
                  </>
                ) : (
                  <>
                    <Download className="inline h-3 w-3 mr-1" />
                    {t('book.downloadOriginal')}
                  </>
                )}
              </button>
            ) : (
              <div></div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDeleteTranslation}
              disabled={deleting}
              className="delete-btn text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 px-2"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* ============ DESKTOP LAYOUT - Two Columns ============ */}
        <div className="desktop-layout hidden lg:block">
          <div className="grid grid-cols-3 gap-6">
            {/* Left Column - Cover + Details */}
            <div className="col-span-1 space-y-6">
              {/* Cover - Full size on desktop */}
              <div className="book-cover-desktop">
                <div className="aspect-[2/3] bg-muted rounded-lg overflow-hidden max-w-[400px]">
                  {book.cover_url ? (
                    <img
                      src={book.cover_url}
                      alt={book.title_international}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}
                </div>
              </div>

              {/* Book Details */}
              <div className="book-details space-y-3">
                {book.genre && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('book.genre')}</h3>
                    <p className="text-foreground">{book.genre}</p>
                  </div>
                )}
                {book.estimated_pages && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('book.estimatedPages')}</h3>
                    <p className="text-foreground">~{book.estimated_pages} {t('book.pages')}</p>
                  </div>
                )}
                {book.original_language && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('book.originalLanguage')}</h3>
                    <p className="text-foreground uppercase">{book.original_language}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Title + Version Info + Actions + Description */}
            <div className="col-span-2 space-y-6">
              {/* Title and Author */}
              <div className="title-section">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-8 bg-primary rounded-full"></div>
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">
                      {book.title_international}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      {t('book.by', 'by')} {book.author_international}
                    </p>
                  </div>
                </div>
              </div>

              {/* Badge */}
              <Badge variant="secondary" className="private-library-badge">
                <BookMarked className="h-3 w-3 mr-1" />
                {t('library.myGradedBook', 'My Graded Book')}
              </Badge>

              {/* Version Info Card (static, no selectors) */}
              <div className="version-card bg-card rounded-lg border p-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">
                  {t('book.translationVersion', 'Translation Version')}
                </h3>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{getLanguageFlag(currentTranslation.language)}</span>
                    <span className="text-lg font-medium">{getLanguageDisplayName(currentTranslation.language)}</span>
                  </div>
                  {currentTranslation.cefr_level && (
                    <Badge variant="default" className="level-badge text-sm px-3 py-1">
                      {currentTranslation.cefr_level}
                    </Badge>
                  )}
                  <Badge variant="outline" className="format-badge">
                    {currentTranslation.file_format.toUpperCase()}
                  </Badge>
                </div>

                {/* Processing status */}
                {isProcessing && (
                  <div className="processing-status mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">
                        {t('book.translationInProgress', 'Translation in progress...')}
                      </span>
                      <span className="text-sm font-medium text-primary">{translationProgress}%</span>
                    </div>
                    <Progress value={translationProgress} className="h-2" />
                  </div>
                )}
              </div>

              {/* Desktop Action Buttons */}
              <div className="desktop-actions">
                {isAvailable ? (
                  <div className="flex items-center gap-3">
                    {canReadOnline && (
                      <Button
                        onClick={handleRead}
                        className="read-btn"
                        size="lg"
                      >
                        <BookOpenCheck className="mr-2 h-5 w-5" />
                        {t('reader.readOnline', 'Read')}
                      </Button>
                    )}
                    <Button
                      onClick={handleDownload}
                      disabled={downloading}
                      variant="outline"
                      className="download-btn"
                      size="icon"
                      title={t('common.download')}
                    >
                      {downloading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Download className="h-5 w-5" />
                      )}
                    </Button>
                    {canPreview && (
                      <Button
                        onClick={handlePreview}
                        variant="ghost"
                        className="preview-btn"
                      >
                        {t('reader.preview', 'Preview')}
                      </Button>
                    )}
                    {/* Delete button - pushed to the right */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleDeleteTranslation}
                      disabled={deleting}
                      className="delete-btn ml-auto text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-9 w-9"
                    >
                      {deleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ) : isProcessing ? (
                  <p className="text-sm text-muted-foreground">
                    {t('book.waitingForTranslation', 'Translation is being processed. Please wait.')}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('book.translationNotAvailable', 'Translation is not available.')}
                  </p>
                )}
                
                {/* Download Original - Small link */}
                {book.original_file_path && (
                  <div className="download-original mt-4">
                    <button
                      onClick={handleDownloadOriginal}
                      disabled={downloadingOriginal}
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                    >
                      {downloadingOriginal ? (
                        <>
                          <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                          {t('library.downloading')}
                        </>
                      ) : (
                        <>
                          <Download className="inline h-3 w-3 mr-1" />
                          {t('book.downloadOriginal')}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Description */}
              {book.description && (
                <div className="description-section">
                  <h3 className="text-lg font-semibold text-foreground mb-2">{t('common.description')}</h3>
                  <p className="text-muted-foreground whitespace-pre-wrap">{book.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Action Bar - Mobile Only */}
      <div className="lg:hidden">
        <div className="sticky-action-bar fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t z-50">
          <div className="max-w-screen-xl mx-auto flex items-center gap-2 p-3">
            {supportsOnlineReading && (
              <Button
                onClick={handleRead}
                disabled={!isAvailable}
                className="read-btn flex-1"
                size="lg"
              >
                <BookOpenCheck className="mr-2 h-5 w-5" />
                {t('reader.readOnline', 'Read')}
              </Button>
            )}
            <Button
              onClick={handleDownload}
              disabled={!isAvailable || downloading}
              variant="outline"
              className="download-btn"
              size="icon"
              title={t('common.download')}
            >
              {downloading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Download className="h-5 w-5" />
              )}
            </Button>
            {supportsOnlineReading && (
              <Button
                onClick={handlePreview}
                disabled={!isAvailable}
                variant="ghost"
                className="preview-btn"
              >
                {t('reader.preview', 'Preview')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
